// src/context/tenantContext.js
//
// 🔑 ÚNICA FONTE DA VERDADE do tenant atual.
//
// Garantias críticas de multi-tenancy:
// 1. Estado em #private fields (ES2022) — NÃO acessível via window/console/XSS.
// 2. Caches centralizados em Map único — clear() limpa TUDO de uma vez.
// 3. config exposto via getter retorna Object.freeze (consumidor não muta).
// 4. Eventos canônicos via eventBus — módulos reagem sem acoplamento.
// 5. NÃO chamamos sb diretamente daqui — isso é responsabilidade do authContext
//    pra evitar ciclo de import.
//
// Como módulos consomem:
//   import { tenant } from '@context/tenantContext';
//   const id = tenant.empresaId;            // null se não logado/super_admin
//   tenant.registerCache('clientes', list); // armazena cache
//   tenant.getCache('clientes');            // lê (válido se TTL não venceu)

import { eventBus } from './eventBus.js';

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 min

class TenantContext {
  #empresaId = null;        // string|null
  #config = null;           // { nome, plano, logo_url, cor_primaria, ... }
  #user = null;             // sb.auth user
  #profile = null;          // row users_profile (nome, tipo_usuario, empresa_id)
  #role = null;             // papel RBAC (user_roles · fallback mapeado do tipo_usuario)
  #roles = [];              // TODOS os papéis (feature 002 — matriz multi-papel)
  #caches = new Map();      // key → { value, expiresAt }
  #hydrated = false;

  // ── leitura segura ────────────────────────────────────────────────
  get empresaId() { return this.#empresaId; }
  get isHydrated() { return this.#hydrated; }
  get isSuperAdmin() { return this.#profile?.tipo_usuario === 'super_admin'; }
  get isAdmin() { return this.#profile?.tipo_usuario === 'administrador'; }
  get isSupervisor() { return this.#profile?.tipo_usuario === 'supervisor'; }
  get isFuncionario() { return this.#profile?.tipo_usuario === 'funcionario'; }
  get isCliente() { return this.#profile?.tipo_usuario === 'cliente'; }
  get isTrial() { return this.#config?.plano === 'trial'; }

  get user() { return this.#user ? Object.freeze({ ...this.#user }) : null; }
  get profile() { return this.#profile ? Object.freeze({ ...this.#profile }) : null; }
  get role() { return this.#role; }
  get roles() { return [...this.#roles]; }
  get config() { return this.#config ? Object.freeze({ ...this.#config }) : null; }

  // ── hidratação · chamada pelo authContext após signIn ───────────────
  /**
   * @param {object} args
   * @param {object} args.user — sb.auth user (com id, email)
   * @param {object} args.profile — row users_profile
   * @param {object|null} args.config — row empresas (null pra super_admin)
   */
  hydrate({ user, profile, config, role, roles }) {
    const prev = this.#empresaId;

    this.#user = user || null;
    this.#profile = profile || null;
    this.#role = role || null;
    this.#roles = Array.isArray(roles) ? roles : (role ? [role] : []);
    this.#empresaId = profile?.empresa_id || null;
    this.#config = config || null;
    this.#hydrated = true;

    eventBus.emit('tenant:hydrated', {
      empresaId: this.#empresaId,
      isSuperAdmin: this.isSuperAdmin,
    });

    if (prev !== this.#empresaId) {
      eventBus.emit('tenant:changed', { empresaId: this.#empresaId, prev });
    }
  }

  // ── caches centralizados ───────────────────────────────────────────
  /**
   * Registra valor em cache namespeado por empresa_id atual.
   * @param {string} key — nome do cache (ex: 'clientes', 'cr-mes')
   * @param {*} value — qualquer valor serializável
   * @param {number} [ttlMs] — TTL em ms (default 5 min)
   */
  registerCache(key, value, ttlMs = DEFAULT_TTL_MS) {
    if (!key) throw new TypeError('cache requer key');
    const ns = this.#cacheKey(key);
    this.#caches.set(ns, { value, expiresAt: Date.now() + ttlMs });
  }

  /**
   * Lê cache. Retorna null se não existe ou TTL venceu.
   */
  getCache(key) {
    const ns = this.#cacheKey(key);
    const entry = this.#caches.get(ns);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.#caches.delete(ns);
      return null;
    }
    return entry.value;
  }

  /**
   * Invalida cache específico (mantém os outros).
   * @param {string|null} key — null limpa TODOS os caches do tenant atual
   */
  invalidateCache(key = null) {
    if (key === null) {
      // limpa só os caches do tenant atual (preserva super_admin se trocar pra empresa)
      const prefix = `t:${this.#empresaId || 'super'}:`;
      for (const k of this.#caches.keys()) {
        if (k.startsWith(prefix)) this.#caches.delete(k);
      }
    } else {
      this.#caches.delete(this.#cacheKey(key));
    }
    eventBus.emit('cache:invalidate', { scope: key });
  }

  #cacheKey(key) {
    return `t:${this.#empresaId || 'super'}:${key}`;
  }

  // ── limpeza total · chamada no signOut ──────────────────────────────
  /**
   * Limpa TUDO. Chamado por authContext.signOut() e por trocar de tenant.
   * Emite 'auth:signedout' pra que módulos limpem seus estados locais.
   */
  clear() {
    this.#empresaId = null;
    this.#config = null;
    this.#user = null;
    this.#profile = null;
    this.#role = null;
    this.#caches.clear();
    this.#hydrated = false;
    eventBus.emit('auth:signedout');
  }

  // ── helper para debug em dev (Vite mode === 'staging') ──────────────
  _debugSnapshot() {
    if (import.meta.env.MODE === 'production') return null;
    return {
      empresaId: this.#empresaId,
      tipo: this.#profile?.tipo_usuario,
      isHydrated: this.#hydrated,
      cacheCount: this.#caches.size,
    };
  }
}

// Singleton exportado · NÃO exposto em window
export const tenant = new TenantContext();
